import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Box,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsOff as NotificationsOffIcon,
  Storage as StorageIcon,
  Delete as DeleteIcon,
  GetApp as InstallIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon
} from '@mui/icons-material';
import {
  requestNotificationPermission,
  isSubscribedToPushNotifications,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications
} from '../utils/pushNotifications';
import { getPendingFormsCount } from '../utils/backgroundSync';

/**
 * @component PWASettings
 * @description Component for managing PWA settings like notifications and offline data
 */
const PWASettings = ({ open, onClose }) => {
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(Notification.permission);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingContactForms, setPendingContactForms] = useState(0);
  const [pendingRegistrationForms, setPendingRegistrationForms] = useState(0);
  const [storageUsage, setStorageUsage] = useState(null);

  // Public VAPID key for push notifications
  const publicVapidKey = 'BLBz4TKmvJdFfTlNZf9eTUMCRWjx5Yd7CGP8g8P9_OMT6jyKZpKxJCDFSdHE6lnK_QKsLpzIzA6JCfn0vP4xR1o';

  useEffect(() => {
    // Check if the app is installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Check notification subscription status
    const checkNotificationStatus = async () => {
      const isSubscribed = await isSubscribedToPushNotifications();
      setNotificationsEnabled(isSubscribed);
    };

    // Check pending forms
    const checkPendingForms = async () => {
      const contactForms = await getPendingFormsCount('contact');
      const registrationForms = await getPendingFormsCount('registration');

      setPendingContactForms(contactForms);
      setPendingRegistrationForms(registrationForms);
    };

    // Estimate storage usage
    const estimateStorageUsage = async () => {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        try {
          const estimate = await navigator.storage.estimate();
          const usageInMB = Math.round(estimate.usage / (1024 * 1024) * 10) / 10;
          const quotaInMB = Math.round(estimate.quota / (1024 * 1024));
          const percentUsed = Math.round((estimate.usage / estimate.quota) * 100);

          setStorageUsage({
            usage: usageInMB,
            quota: quotaInMB,
            percent: percentUsed
          });
        } catch (error) {
          console.error('Error estimating storage usage:', error);
        }
      }
    };

    // Function to handle online status change
    const handleOnlineStatusChange = () => {
      setIsOnline(navigator.onLine);
    };

    // Add event listeners
    window.addEventListener('online', handleOnlineStatusChange);
    window.addEventListener('offline', handleOnlineStatusChange);

    // Initial checks
    checkNotificationStatus();
    checkPendingForms();
    estimateStorageUsage();

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnlineStatusChange);
      window.removeEventListener('offline', handleOnlineStatusChange);
    };
  }, []);

  const handleNotificationToggle = async () => {
    if (notificationsEnabled) {
      // Unsubscribe from push notifications
      const success = await unsubscribeFromPushNotifications();
      if (success) {
        setNotificationsEnabled(false);
      }
    } else {
      // Request permission and subscribe to push notifications
      const permissionGranted = await requestNotificationPermission();
      setNotificationPermission(Notification.permission);

      if (permissionGranted) {
        const subscription = await subscribeToPushNotifications(publicVapidKey);
        setNotificationsEnabled(!!subscription);
      }
    }
  };

  const handleClearCache = async () => {
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        alert('Cache cleared successfully');
      } catch (error) {
        console.error('Error clearing cache:', error);
        alert('Error clearing cache');
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <Typography variant="h6">App Settings</Typography>
      </DialogTitle>

      <DialogContent>
        {/* Installation Status */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Installation Status
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <InstallIcon color="primary" sx={{ mr: 1 }} />
            <Typography>
              {isInstalled ? 'App is installed' : 'App is not installed'}
            </Typography>
          </Box>

          {!isInstalled && (
            <Alert severity="info" sx={{ mt: 1 }}>
              You can install this app on your device for a better experience.
            </Alert>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Connection Status */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Connection Status
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isOnline ? (
              <>
                <WifiIcon color="success" sx={{ mr: 1 }} />
                <Typography>Online - Full functionality available</Typography>
              </>
            ) : (
              <>
                <WifiOffIcon color="warning" sx={{ mr: 1 }} />
                <Typography>Offline - Limited functionality available</Typography>
              </>
            )}
          </Box>

          {!isOnline && (pendingContactForms > 0 || pendingRegistrationForms > 0) && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              You have pending form submissions that will be sent when you're back online.
            </Alert>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Notifications */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Notifications
          </Typography>

          {notificationPermission === 'denied' ? (
            <Alert severity="warning">
              Notifications are blocked. Please enable them in your browser settings.
            </Alert>
          ) : (
            <FormControlLabel
              control={
                <Switch
                  checked={notificationsEnabled}
                  onChange={handleNotificationToggle}
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {notificationsEnabled ? (
                    <NotificationsIcon color="primary" sx={{ mr: 1 }} />
                  ) : (
                    <NotificationsOffIcon sx={{ mr: 1 }} />
                  )}
                  <Typography>
                    {notificationsEnabled ? 'Notifications enabled' : 'Notifications disabled'}
                  </Typography>
                </Box>
              }
            />
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Storage and Offline Data */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Storage and Offline Data
          </Typography>

          <List dense>
            {storageUsage && (
              <ListItem>
                <ListItemIcon>
                  <StorageIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Storage Usage"
                  secondary={`${storageUsage.usage} MB of ${storageUsage.quota} MB (${storageUsage.percent}%)`}
                />
              </ListItem>
            )}

            <ListItem>
              <ListItemIcon>
                <StorageIcon />
              </ListItemIcon>
              <ListItemText
                primary="Pending Contact Forms"
                secondary={`${pendingContactForms} form(s) waiting to be submitted`}
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <StorageIcon />
              </ListItemIcon>
              <ListItemText
                primary="Pending Registration Forms"
                secondary={`${pendingRegistrationForms} form(s) waiting to be submitted`}
              />
            </ListItem>
          </List>

          <Button
            variant="outlined"
            color="warning"
            startIcon={<DeleteIcon />}
            onClick={handleClearCache}
            sx={{ mt: 2 }}
          >
            Clear Cache
          </Button>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PWASettings;
